import * as XLSX from "xlsx";
import moment from "moment";

// Sheet headers stay English regardless of the UI language.
const ROW_NUMBER_HEADER = "Row Number";
// The rejected row's `identifier` is the meter/serial number the sheet supplied.
const METER_NUMBER_HEADER = "Meter Number";
const STATUS_HEADER = "Status";
const REASON_HEADER = "Reason";

const HEADERS = [ROW_NUMBER_HEADER, METER_NUMBER_HEADER, STATUS_HEADER, REASON_HEADER];

/**
 * Strip the extension off an uploaded sheet name so it can prefix the export
 * file name (`Stock Details.xlsx` -> `Stock Details`).
 * @param {string} fileName - Original uploaded file name.
 * @returns {string} The name without its extension.
 */
const withoutExtension = (fileName = "") => fileName.replace(/\.[^./\\]+$/, "");

/**
 * Build the export file name for an upload run, falling back to the upload id
 * when the run carries no file name.
 * @param {Object} upload - Upload row from the upload-list/status payload.
 * @returns {string} File name prefix for the generated sheet.
 */
export const rejectedRowsFileNamePrefix = (upload = {}) => {
  const base =
    withoutExtension(upload.fileName || "") ||
    (upload.id ?? upload.uploadId ?? upload.inventoryUploadId
      ? `upload-${upload.id ?? upload.uploadId ?? upload.inventoryUploadId}`
      : "inventory-upload");
  return `${base}-not-uploaded`;
};

/**
 * Build a four column (row / identifier / status / reason) sheet from the rows a
 * run rejected and trigger an immediate download. Field names are read
 * defensively because the inline `notUploaded` array and the paged rows endpoint
 * have used slightly different shapes.
 * @param {Array} rejectedRows - Entries of `{ rowNumber, identifier, rowStatus, reason }`,
 *   where `identifier` is the meter number.
 * @param {string} fileNamePrefix - Prefix for the generated .xlsx file name.
 * @returns {boolean} False when there was nothing to export.
 */
export const downloadRejectedRowsExcel = (
  rejectedRows,
  fileNamePrefix = "inventory-upload-not-uploaded"
) => {
  if (!Array.isArray(rejectedRows) || rejectedRows.length === 0) return false;

  const rows = rejectedRows.map((item) => ({
    [ROW_NUMBER_HEADER]: item?.rowNumber ?? item?.row ?? item?.rowIndex ?? "",
    [METER_NUMBER_HEADER]: item?.identifier ?? item?.serialNumber ?? item?.meterNumber ?? "",
    [STATUS_HEADER]: item?.rowStatus ?? item?.status ?? "",
    [REASON_HEADER]: item?.reason ?? item?.message ?? item?.error ?? "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows, { header: HEADERS });
  worksheet["!cols"] = [{ wch: 12 }, { wch: 24 }, { wch: 12 }, { wch: 80 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Not Uploaded");
  XLSX.writeFile(
    workbook,
    `${fileNamePrefix}-${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`
  );
  return true;
};
